/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, {useEffect, useState} from 'react';
import {Form, Formik, FormikHelpers} from 'formik';
import {StyledProvisioningPageHeading} from '../StyledProvisioningPageHeading';
import {StyledProvisioningPageSubheading} from '../StyledProvisioningPageSubheading';
import {Box, Stack} from '@mui/material';
import {useHistory, useParams} from 'react-router-dom';
import {BeamInput} from '../../common/formik/BeamInput';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectRoles, selectRolesRequestError } from '../../../store';
import { BeamSelect } from '../../common/formik/BeamSelect';
import { BeamCancelButton } from '../../common/formik/BeamCancelButton';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import { User } from '../../../shared/entities/user';
import ModalDialog from '../../common/dialogs/ModalDialog';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { requestRoles } from '../../../store/roles/actions';
import { Organization } from '../../../shared/entities/organization';

import { ajaxRaw } from '../../../utils/StoreUtils';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';

import * as yup from 'yup';

import RoleHelpText from '../RoleHelpText';

const UserForm = () => {
  const EMAIL_PATTERN =
    '^(?=.{1,64}@)[A-Za-z0-9_-]+(\\.[A-Za-z0-9_-]+){0,64}(\\+[A-Za-z0-9_-]{1,64})?@' +
    '[A-Za-z0-9-]+(\\.[A-Za-z0-9-]+){0,64}(\\.[A-Za-z]{2,})$';
  const history = useHistory();
  const dispatch = useDispatch();
  let { uuid } = useParams();

  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';
  const rolesList = useSelector(selectRoles, shallowEqual);
  const rolesRequestError = useSelector(selectRolesRequestError, shallowEqual);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [orgMap, setOrgMap] = useState<Map<string, number>>();

  const [initialFormState, setInitialFormState] = useState<User>({
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    roles: [],
    rolesInheritedFromOrg: [],
    orgNum: 0,
    orgName: '',
  });

  // Actions on initial load
  useEffect(() => {
    dispatch(requestRoles());
    ajaxRaw.get(`/provisioning/organizations`).subscribe({
      next: value => {
        setOrganizations(value.response);
        setOrgMap(new Map(value.response.map((org: Organization) => [org.name, org.orgNum])));
      },
      error: err => setError(err.message),
    });
  }, [dispatch]);

  // Request initial data when editing
  useEffect(() => {
    if (isEditMode) {
      ajaxRaw.get(`/provisioning/users/${uuid}`).subscribe({
        next: value =>
          setInitialFormState({
            ...value.response,
            roles: value.response.roles.map((value: string) => value.replace(/^ROLE_/g, '')),
          }),
        error: err => setError(err.message),
      });
    }
  }, [isEditMode, uuid]);

  // Update error message if an error occurs fetching roles
  useEffect(() => {
    if (rolesRequestError) {
      setError(rolesRequestError);
    }
  }, [rolesRequestError]);

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}user
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}user, please complete the form below.
      </StyledProvisioningPageSubheading>

      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<User>) => {
          setError(undefined);
          setSubmitting(true);
          let url = `/provisioning/users`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/users/${values.id}`;
            operation = ajaxRaw.put;
          }

          let orgNum = orgMap!.get(values.orgName);
          values.orgNum = orgNum ? orgNum : 0;

          operation(url, JSON.stringify(values)).subscribe({
            next: (response: AjaxResponse<any>) => {
              setSubmitting(false);
              if (response.status !== 200 && response.status !== 201) {
                setError(response.response);
              }
              setShowModal(true);
            },
            error: (err: AjaxError) => {
              setError(err.response.message ? err.response.message : err.message);
              setSubmitting(false);
              setShowModal(true);
            },
          });
        }}
        validateOnChange={false}
        validationSchema={yup.object().shape({
          firstName: yup
            .string()
            .required('Please enter a first name.')
            .max(255, 'First name must be less than 255 characters.'),
          lastName: yup
            .string()
            .required('Please enter a last name')
            .max(255, 'Last name must be less than 255 characters.'),
          email: yup
            .string()
            .required('Please enter an email address.')
            .max(255, 'Email address must be less than 255 characters.')
            .matches(new RegExp(EMAIL_PATTERN), 'Please enter a valid email address.'),
          roles: yup.array().of(yup.string()),
        })}
      >
        <Form style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <BeamInput name={'lastName'} label={'Last Name'} required={true} />
          <BeamInput name={'firstName'} label={'First Name'} required={true} />
          <BeamInput
            name={'email'}
            label={'Email Address'}
            required={!isEditMode}
            disableInput={isEditMode}
            helpText={isEditMode ? '' : 'Email address cannot be changed once created.'}
          />

          <BeamSelect
            name={'orgName'}
            label={'Organization'}
            options={organizations ? organizations.map(org => org.name).sort((a, b) => a.localeCompare(b)) : []}
            placeholder={'Select an option'}
            required={true}
          />

          <BeamMultiSelect
            name={'roles'}
            options={rolesList ? rolesList.map(role => role.name) : []}
            label={'Roles'}
            helpText={
              <>
                This user will also inherit roles defined at the organization level. <RoleHelpText />
              </>
            }
          />

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <BeamCancelButton />
            <BeamSubmitButton />
          </Stack>
          <ModalDialog
            open={showModal}
            hideLeftBtn={true}
            rightBtnText="Ok"
            rightBtnColor="#006EAD"
            rightBtnTextColor="#FFFFFF"
            onLeftBtnClicked={() => {}}
            onRightBtnClicked={() => {
              if (submissionError) {
                new Promise((resolve, reject) => {
                  setShowModal(false);
                }).then(() => {
                  setError(undefined);
                });
              } else {
                history.goBack();
              }
            }}
            title={
              <Box display={'flex'} gap={'8px'} alignItems={'center'}>
                {submissionError ? (
                  <>
                    {' '}
                    <WarningAmberIcon htmlColor={'#E73737'} /> Account Creation Failed{' '}
                  </>
                ) : (
                  <>
                    {' '}
                    <CheckCircleOutlineIcon htmlColor={'#5EA215'} /> Account {isEditMode ? 'Edited' : 'Created'}{' '}
                  </>
                )}
              </Box>
            }
            message={
              submissionError ? (
                <>
                  A failure occured while {isEditMode ? 'editing' : 'creating'} this account.
                  <br />
                  {submissionError}
                </>
              ) : (
                <>
                  {isEditMode
                    ? 'Successfully edited user.'
                    : 'This user may now login to Unified Portal using their existing credentials.'}
                </>
              )
            }
          />
        </Form>
      </Formik>
    </>
  );
};

export default UserForm;
